import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { ArrowDownwardOutlined, ArrowUpwardOutlined } from "@mui-symbols-material/w300"
import type { SortingState } from "@tanstack/react-table"
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table"
import type { FC } from "react"
import { useState } from "react"
import { twMerge } from "tailwind-merge"

import { AKROCHEM_GROUP_ID, useProductWarehouseColumns } from "../hooks/useProductWarehouseColumns"

import type { ProductWarehouseArea } from "@/graphql/codegen/graphql"

interface ProductWarehouseTableProps {
  warehouseAreas: ProductWarehouseArea[]
  isPastProductVersion: boolean
  packageUsUomName?: string | null
  onEdit: (warehouseArea: ProductWarehouseArea) => void
}

export const ProductWarehouseTable: FC<ProductWarehouseTableProps> = ({
  warehouseAreas,
  isPastProductVersion,
  packageUsUomName,
  onEdit,
}) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: "externalSystemId", desc: false }])

  const { columns } = useProductWarehouseColumns({ isPastProductVersion, packageUsUomName, onEdit })

  const table = useReactTable({
    data: warehouseAreas,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableMultiSort: false,
    enableSortingRemoval: false,
  })

  return (
    <TableContainer>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              sx={{
                ":not(:last-child) .MuiTableCell-head": { borderBottom: 0 },
                ":not(:first-child) .MuiTableCell-head": { borderTop: 0, borderRadius: "0 !important" },
              }}
            >
              {headerGroup.headers.map((header) => {
                const isAkrochemGroup =
                  header.column.id === AKROCHEM_GROUP_ID || header.column.parent?.id === AKROCHEM_GROUP_ID
                const canSort = header.column.getCanSort()
                const sorted = header.column.getIsSorted()

                return (
                  <TableCell
                    key={header.id}
                    colSpan={header.colSpan}
                    className={twMerge(
                      isAkrochemGroup ? "bg-gray-300" : "",
                      canSort ? "cursor-pointer select-none" : ""
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <div className='flex items-center gap-x-2'>
                        {canSort && sorted === "asc" && <ArrowUpwardOutlined fontSize='small' />}
                        {canSort && sorted === "desc" && <ArrowDownwardOutlined fontSize='small' />}
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} data-testid={`product.warehouse-table.${row.id}-${cell.column.id}`}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
